// pages/Habitaciones.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Spin,
  Card,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  HomeOutlined,
  ExportOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  FlagOutlined,
  StarOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

const Habitaciones = () => {
  // Estados
  const [habitaciones, setHabitaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentHabitacion, setCurrentHabitacion] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  const [habitacionesVenta, setHabitacionesVenta] = useState([]);
  const [loadingHabitacionesVenta, setLoadingHabitacionesVenta] = useState(false);  

  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Estado para paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} habitaciones`
  });

  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'Número',
      dataIndex: 'numhab',
      key: 'numhab',
      sorter: (a, b) => a.numhab - b.numhab,
      sortOrder: sortedInfo.columnKey === 'numhab' && sortedInfo.order,
      width: 100,
      render: (numhab) => (
        <Tag color="blue" style={{ fontWeight: 'bold', fontSize: '14px' }}>
          {numhab}
        </Tag>
      ),
    },
    {
      title: 'Piso',
      dataIndex: 'piso',
      key: 'piso',
      sorter: (a, b) => a.piso - b.piso,
      sortOrder: sortedInfo.columnKey === 'piso' && sortedInfo.order,
      width: 80,
      render: (piso) => (
        <Tag color="geekblue" style={{ fontSize: '12px' }}>
          Piso {piso}
        </Tag>
      ),
    },
    {
      title: 'Tipo Hab.',
      dataIndex: 'tipohab',
      key: 'tipohab',
      width: 100,
      sorter: (a, b) => a.tipohab?.localeCompare(b.tipohab || '') || 0,
      sortOrder: sortedInfo.columnKey === 'tipohab' && sortedInfo.order,
      render: (tipohab) => (
        <Tag color="purple">{tipohab || 'N/A'}</Tag>
      ),
    },
    {
      title: 'Descripción',
      dataIndex: 'descrip',
      key: 'descrip',
      ellipsis: true,
      width: 200,
      render: (descrip) => descrip || <span style={{ color: '#999' }}>Sin descripción</span>,
    },
    {
      title: 'Hab. Venta',
      dataIndex: 'HabitacionVenta2',
      key: 'HabitacionVenta2',
      width: 120,
      render: (HabitacionVenta2, record) => {
        if (!HabitacionVenta2) {
          return <Tag color="default">No asignada</Tag>;
        }
        
        // Buscar la habitación de venta en el estado
        const habVenta = habitacionesVenta.find(h => h.Codigo === HabitacionVenta2);
        
        return (
          <Tooltip 
            title={habVenta ? `${habVenta.Descripcion}` : `Código: ${HabitacionVenta2}`}
          >
            <Tag color="green" style={{ cursor: 'help' }}>
              {HabitacionVenta2}
              {habVenta?.porDefecto && (
                <StarOutlined style={{ marginLeft: 4, fontSize: '12px', color: '#faad14' }} />
              )}
            </Tag>
          </Tooltip>
        );
      },
      filters: [
        { text: 'Asignada', value: 'hasValue' },
        { text: 'No asignada', value: 'noValue' },
      ],
      filteredValue: filteredInfo.HabitacionVenta2 || null,
      onFilter: (value, record) => {
        if (value === 'hasValue') return !!record.HabitacionVenta2;
        if (value === 'noValue') return !record.HabitacionVenta2;
        return true;
      },
    },    
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 100,
      render: (estado) => {
        const estadoMap = {
          0: { text: 'Limpia', color: 'green', badge: 'success' },
          1: { text: 'Sucia', color: 'orange', badge: 'warning' },
          2: { text: 'Bloqueada', color: 'red', badge: 'error' },          
          3: { text: 'Mantenimiento', color: 'volcano', badge: 'default' },
          4: { text: 'Limpieza', color: 'gray', badge: 'default' }
        };
        const estadoInfo = estadoMap[estado] || { text: 'Desconocido', color: 'gray' };
        return (
          <Badge
            status={estadoInfo.badge}
            text={estadoInfo.text}
          />
        );
      },
      filters: [
        { text: 'Limpia', value: 0 },
        { text: 'Limpieza', value: 4 },
        { text: 'Sucia', value: 1 },
        { text: 'Mantenimiento', value: 3 },
        { text: 'Bloqueada', value: 2 },
      ],
      filteredValue: filteredInfo.estado || null,
      onFilter: (value, record) => record.estado === value,
    },
    {
      title: 'Activa',
      dataIndex: 'activo',
      key: 'activo',
      width: 90,
      render: (activo) => (
        activo ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
        )
      ),
      filters: [
        { text: 'Activa', value: true },
        { text: 'Inactiva', value: false },
      ],
      filteredValue: filteredInfo.activo || null,
      onFilter: (value, record) => record.activo === value,
    },
    {
      title: 'Es Fantasma',
      dataIndex: 'esFantasma',
      key: 'esFantasma',
      width: 110,
      render: (esFantasma) => (
        esFantasma ? (
          <Tag color="orange">Fantasma</Tag>
        ) : (
          <Tag color="default">Normal</Tag>
        )
      ),
      filters: [
        { text: 'Fantasma', value: true },
        { text: 'Normal', value: false },
      ],
      filteredValue: filteredInfo.esFantasma || null,
      onFilter: (value, record) => record.esFantasma === value,
    },
    {
      title: 'Resaltar',
      dataIndex: 'ResaltarHabitacion',
      key: 'ResaltarHabitacion',
      width: 100,
      render: (resaltar) => (
        resaltar ? (
          <StarOutlined style={{ color: '#faad14', fontSize: '18px' }} />
        ) : (
          <StarOutlined style={{ color: '#d9d9d9', fontSize: '18px' }} />
        )
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar habitación?"
            description="Esta acción eliminará la habitación permanentemente"
            onConfirm={() => handleDelete(record.numhab, record.codigohotel)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchHabitaciones();
    fetchHabitacionesVenta();
  }, []);

  const fetchHabitaciones = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log(" sortedInfo.order => ",sortedInfo.order);
      
      // Parámetros de la query
      const queryParams = {
        page: params.current || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'numhab',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };
      
      // Añadir filtros
      if (filteredInfo.estado) {
        queryParams.estado = filteredInfo.estado[0];
      }
      if (filteredInfo.codigohotel) {
        queryParams.codigohotel = filteredInfo.codigohotel[0];
      }
      if (filteredInfo.activo) {
        queryParams.activo = filteredInfo.activo[0];
      }
      if (filteredInfo.esFantasma) {
        queryParams.esFantasma = filteredInfo.esFantasma[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/habitaciones`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        descrip: item.descrip || '',
        tipohab: item.tipohab || '',
        HabitacionEspecial: item.HabitacionEspecial || '',
        HabitacionVenta2: item.HabitacionVenta2 || '',
        HabitacionChannel: item.HabitacionChannel || '',
        activo: Boolean(item.activo),
        esFantasma: Boolean(item.esFantasma),
        ResaltarHabitacion: Boolean(item.ResaltarHabitacion)
      }));
      
      setHabitaciones(cleanedData);
      
      // Actualizar paginación
      setPagination({
        ...pagination,
        current: response.data.pagination?.currentPage || 1,
        pageSize: response.data.pagination?.pageSize || pagination.pageSize,
        total: response.data.pagination?.totalRecords || cleanedData.length
      });
      
    } catch (error) {
      console.error('❌ Error cargando habitaciones:', error);
      message.error('Error al cargar las habitaciones');
      setHabitaciones([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchHabitacionesVenta = async () => {
    setLoadingHabitacionesVenta(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/habitacionesventa`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar solo las activas y ordenar
      const habitacionesActivas = (response.data.data || [])
        .filter(h => h.Activo)
        .sort((a, b) => a.Descripcion.localeCompare(b.Descripcion));
      
      setHabitacionesVenta(habitacionesActivas);
    } catch (error) {
      console.error('❌ Error cargando habitaciones de venta:', error);
      message.error('Error al cargar las habitaciones de venta');
      setHabitacionesVenta([]);
    } finally {
      setLoadingHabitacionesVenta(false);
    }
  };  

  const handleTableChange = (newPagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    const params = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sortField: sorter.field,
      sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
    };
    
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
    
    fetchHabitaciones(params);
  };

  // Handlers para las acciones
  const handleCreate = async () => {
    setModalType('create');
    setCurrentHabitacion(null);
    form.resetFields();
    
    // Establecer valores por defecto
    form.setFieldsValue({
      estado: 0,
      activo: true,
      esFantasma: false,
      ResaltarHabitacion: false,
      piso: 0
    });
    
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentHabitacion(record);
    
    const formValues = {
      numhab: record.numhab,
      piso: record.piso || 0,
      tipohab: record.tipohab || '',
      descrip: record.descrip || '',
      estado: record.estado || 0,
      esFantasma: record.esFantasma || false,
      HabitacionEspecial: record.HabitacionEspecial || '',
      HabitacionVenta2: record.HabitacionVenta2 || '',
      codigohotel: record.codigohotel,
      activo: record.activo,
      ResaltarHabitacion: record.ResaltarHabitacion || false,
      HabitacionChannel: record.HabitacionChannel || ''
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleView = (record) => {
    const estadoMap = {
      0: { text: 'Limpia', color: 'green' },
      1: { text: 'Sucia', color: 'orange' },
      2: { text: 'Bloqueada', color: 'red' },
      3: { text: 'Mantenimiento', color: 'volcano' },
      4: { text: 'Limpieza', color: 'gray' }
    };
    const estadoInfo = estadoMap[record.estado] || { text: 'Desconocido', color: 'gray' };
    
    Modal.info({
      title: `Habitación ${record.numhab}`,
      width: 600,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <strong>Número:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '16px' }}>
                  {record.numhab}
                </Tag>
              </div>
            </Col>
            <Col span={8}>
              <strong>Piso:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="geekblue">Piso {record.piso}</Tag>
              </div>
            </Col>
            <Col span={8}>
              <strong>Hotel:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="cyan">
                  <HomeOutlined /> {record.codigohotel}
                </Tag>
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Tipo:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="purple">{record.tipohab || 'No especificado'}</Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Estado:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color={estadoInfo.color}>{estadoInfo.text}</Tag>
              </div>
            </Col>
            
            <Col span={24}>
              <strong>Descripción:</strong>
              <div style={{ marginTop: 4, padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                {record.descrip || 'Sin descripción'}
              </div>
            </Col>
            
            <Col span={8}>
              <strong>Activa:</strong>
              <div style={{ marginTop: 4 }}>
                {record.activo ? (
                  <Tag color="green">Sí</Tag>
                ) : (
                  <Tag color="red">No</Tag>
                )}
              </div>
            </Col>
            <Col span={8}>
              <strong>Es Fantasma:</strong>
              <div style={{ marginTop: 4 }}>
                {record.esFantasma ? (
                  <Tag color="orange">Sí</Tag>
                ) : (
                  <Tag color="default">No</Tag>
                )}
              </div>
            </Col>
            <Col span={8}>
              <strong>Resaltar:</strong>
              <div style={{ marginTop: 4 }}>
                {record.ResaltarHabitacion ? (
                  <Tag color="gold"><StarOutlined /> Sí</Tag>
                ) : (
                  <Tag color="default">No</Tag>
                )}
              </div>
            </Col>
            
            {record.HabitacionEspecial && (
              <Col span={12}>
                <strong>Habitación Especial:</strong>
                <div style={{ marginTop: 4 }}>
                  <Tag color="magenta">{record.HabitacionEspecial}</Tag>
                </div>
              </Col>
            )}
            
            {record.HabitacionVenta2 && (
              <Col span={12}>
                <strong>Habitación Venta:</strong>
                <div style={{ marginTop: 4 }}>
                  {(() => {
                    const habVenta = habitacionesVenta.find(h => h.Codigo === record.HabitacionVenta2);
                    return (
                      <div>
                        <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                          {record.HabitacionVenta2}
                          {habVenta?.porDefecto && (
                            <StarOutlined style={{ marginLeft: 4, fontSize: '12px', color: '#faad14' }} />
                          )}
                        </Tag>
                        {habVenta && (
                          <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
                            {habVenta.Descripcion}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </Col>
            )}
            
            {record.HabitacionChannel && (
              <Col span={12}>
                <strong>Habitación Channel:</strong>
                <div style={{ marginTop: 4 }}>
                  <Tag color="blue">{record.HabitacionChannel}</Tag>
                </div>
              </Col>
            )}
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (numhab, codigohotel) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/habitaciones/${numhab}/${codigohotel}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filtrar el eliminado
      setHabitaciones(habitaciones.filter(h => !(h.numhab === numhab && h.codigohotel === codigohotel)));
      
      // Actualizar total
      setPagination(prev => ({
        ...prev,
        total: prev.total - 1
      }));
      
      message.success('Habitación eliminada correctamente');
    } catch (error) {
      console.error('❌ Error eliminando habitación:', error);
      message.error(error.response?.data?.error || 'Error al eliminar la habitación');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/habitaciones`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchHabitaciones();
        message.success('Habitación creada correctamente');
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/habitaciones/${currentHabitacion.numhab}/${currentHabitacion.codigohotel}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Actualizar el estado local
        const habitacionActualizada = {
          ...response.data.data,
          ...values
        };
        
        setHabitaciones(prevHabitaciones => 
          prevHabitaciones.map(habitacion => 
            habitacion.numhab === currentHabitacion.numhab && 
            habitacion.codigohotel === currentHabitacion.codigohotel
              ? habitacionActualizada 
              : habitacion
          )
        );
        
        message.success('Habitación actualizada correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error guardando habitación:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    setPagination({ ...pagination, current: 1 });
    fetchHabitaciones({ current: 1 });
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    setPagination({ ...pagination, current: 1 });
    fetchHabitaciones({ current: 1 });
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por número, descripción, tipo..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
        <span style={{ color: '#666', fontSize: '12px' }}>
          {pagination.total.toLocaleString()} habitaciones
        </span>
      </Space>
    </div>
  );

  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Habitaciones" 
          value={pagination.total}
          prefix={<HomeOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Página" 
          value={pagination.current}
          suffix={`/ ${Math.ceil(pagination.total / pagination.pageSize)}`}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Habitaciones"
        subtitle={`${pagination.total.toLocaleString()} habitaciones registradas`}
        icon={<HomeOutlined />}
        loading={loading}
        onRefresh={() => fetchHabitaciones()}
        onCreate={handleCreate}
        badgeCount={pagination.total}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{pagination.total.toLocaleString()}</strong> habitaciones
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Barra de búsqueda */}
        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={habitaciones}
          rowKey={(record) => `${record.numhab}-${record.codigohotel}`}
          loading={loading}
          onChange={handleTableChange}
          pagination={pagination}
          scroll={{ x: 1500, y: 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron habitaciones'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar habitación */}
      <Modal
        title={modalType === 'create' ? 'Nueva Habitación' : 'Editar Habitación'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600} // Reducido de 700 a 600
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            estado: 0,
            activo: true,
            esFantasma: false,
            ResaltarHabitacion: false,
            piso: 0
          }}
        >
          {/* Primera fila: Número, Piso y Tipo */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="numhab"
                label="Número de Habitación"
                rules={[{ 
                  required: true, 
                  message: 'Por favor ingrese el número',
                  type: 'integer',
                  min: 1
                }]}
              >
                <InputNumber 
                  style={{ width: '90%' }}
                  min={1}
                  max={9999}
                  placeholder="Ej: 101, 205"
                  disabled={modalType === 'edit'}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="piso"
                label="Piso"
                rules={[{ 
                  type: 'integer',
                  min: 0,
                  max: 99
                }]}
              >
                <InputNumber 
                  style={{ width: '90%' }}
                  min={0}
                  max={99}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="tipohab"
                label="Tipo de Habitación"
                rules={[{ 
                  required: true,
                  message: 'Por favor seleccione el tipo de habitación',
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject(new Error('El tipo de habitación es requerido'));
                    }
                    const tiposValidos = ['IN', 'DO', 'TR', 'CU', 'QU', 'MU'];
                    if (!tiposValidos.includes(value.toUpperCase())) {
                      return Promise.reject(new Error('Tipo inválido. Use: IN, DO, TR, CU, QU o MU'));
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Select placeholder="Seleccione tipo" showSearch allowClear style={{ width: '90%' }}>
                  <Option value="IN">IN - Individual</Option>
                  <Option value="DO">DO - Doble</Option>
                  <Option value="TR">TR - Triple</Option>
                  <Option value="CU">CU - Cuádruple</Option>
                  <Option value="QU">QU - Quintuple</Option>
                  <Option value="MU">MU - Múltiple</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Segunda fila: Descripción (ahora Input normal) */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="descrip"
                label="Descripción"
                rules={[{ 
                  max: 10
                }]}
              >
                <Input 
                  placeholder="Descripción de la habitación"
                  maxLength={10}
                  showCount
                  style={{ width: '90%' }}
                />
              </Form.Item>
            </Col>
          </Row>         

          {/* Campos especiales - Uno debajo del otro */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="HabitacionEspecial"
                label="Habitación Especial"
                rules={[{ max: 10 }]}
                style={{ marginBottom: 16 }}
              >
                <Input 
                  placeholder="Código de habitación especial" style={{ width: '90%' }}
                  maxLength={10}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="HabitacionVenta2"
                label="Habitación de Venta"
                tooltip="Seleccione la habitación de venta asociada"
                style={{ marginBottom: 16 }}
              >
                <Select
                  placeholder="Seleccione habitación de venta" style={{ width: '90%' }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  loading={loadingHabitacionesVenta}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  notFoundContent={loadingHabitacionesVenta ? <Spin size="small" /> : "No hay opciones"}
                >
                  <Option value="">Sin asignar</Option>
                  {habitacionesVenta.map(habitacion => (
                    <Option 
                      key={habitacion.Codigo} 
                      value={habitacion.Codigo}
                      title={`${habitacion.Codigo} - ${habitacion.Descripcion}`}
                    >
                      {habitacion.Codigo} - {habitacion.Descripcion}
                      {habitacion.porDefecto && (
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                          (Por defecto)
                        </Text>
                      )}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="HabitacionChannel"
                label="Habitación Channel"
                rules={[{ max: 10 }]}
                style={{ marginBottom: 16 }}
              >
                <Input 
                  placeholder="Código de habitación channel" style={{ width: '90%' }}
                  maxLength={10}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Switches en una fila */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="activo"
                label="Activa"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="esFantasma"
                label="Es Fantasma"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="ResaltarHabitacion"
                label="Resaltar"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Nota:</strong> El número de habitación debe ser único por hotel.
                <div style={{ marginTop: 8 }}>
                  <strong>Tipos de habitación:</strong>
                  <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                    <li><strong>IN</strong> - Individual (1 persona)</li>
                    <li><strong>DO</strong> - Doble (2 personas)</li>
                    <li><strong>TR</strong> - Triple (3 personas)</li>
                    <li><strong>CU</strong> - Cuádruple (4 personas)</li>
                    <li><strong>QU</strong> - Quintuple (5 personas)</li>
                    <li><strong>MU</strong> - Múltiple (6+ personas)</li>
                  </ul>
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Estados:</strong>
                  <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                    <li><strong>0</strong> = Libre</li>
                    <li><strong>1</strong> = Ocupada</li>
                    <li><strong>2</strong> = Sucio</li>
                    <li><strong>3</strong> = Mantenimiento</li>
                    <li><strong>4</strong> = Bloqueada</li>
                  </ul>
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Campos especiales:</strong>
                  <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                    <li><strong>Habitación Especial</strong>: Código para habitaciones con características especiales</li>
                    <li><strong>Habitación de Venta</strong>: Tipo comercial de la habitación</li>
                    <li><strong>Habitación Channel</strong>: Código para canales de distribución</li>
                  </ul>
                </div>
              </div>
            </Card>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default Habitaciones;